import { Page, PageBody, ToastContainer } from '@pypestream/design-system';
import { CSSProperties, FC, useEffect, useState } from 'react';
import { isProduction } from '@pypestream/utils';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  RouterProvider as ReactRouterProvider,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { Loader, PrimaryNav, ErrorBoundary } from './components';
import {
  StudioPageProps,
  useCurrentRoute,
  useLoadingState,
  useProStudioRoutes,
  useTableStoreSync,
} from './hooks';
import { ErrorPage } from './pages';
import { syncXstateWithRouteParams } from './utils';
import {
  useProStudioCtxSelector,
  useProStudioStateMatches,
} from './xstate/app.xstate';

const centeredContainerStyles: CSSProperties = {
  position: 'absolute',
  left: 0,
  width: '100%',
  height: '100%',
  // @todo - discuss before getting this merged
  top: 64,
  maxHeight: 'calc(100% - 64px)',
  // discuss above 2 lines before getting this merged
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const Root: FC = () => {
  useTableStoreSync();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    home: { path: homePath },
  } = useProStudioRoutes();

  const isAccessDenied = useProStudioStateMatches('accessDenied');

  const { projectId } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
  }));

  useEffect(() => {
    if (pathname === '/' && homePath) {
      navigate(homePath);
    }
  }, [pathname, projectId, homePath, navigate]);

  return (
    <ErrorBoundary>
      <Page>
        <PrimaryNav />

        {isAccessDenied ? (
          <ErrorPage
            label="It looks like you don't have access..."
            body="Contact an admin to request access."
          />
        ) : (
          <Outlet />
        )}
        <ToastContainer />
      </Page>
    </ErrorBoundary>
  );
};

export const RouterProvider = () => {
  const routes = useProStudioRoutes();
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="/"
        element={<Root />}
        loader={({ params, request }) => {
          const url = new URL(request.url);
          const debugValue = url.searchParams.get('debug');

          let debugMode: boolean | undefined = undefined;

          if (debugValue === 'on' || debugValue === 'true') {
            debugMode = true;
          } else if (debugValue === 'off' || debugValue === 'false') {
            debugMode = false;
          } else {
            debugMode = !isProduction;
          }

          syncXstateWithRouteParams({
            params,
            url: new URL(request.url),
            debugMode,
          });
          return null;
        }}
        shouldRevalidate={() => {
          return true;
        }}
      >
        <Route index element={routes.home.element} />
        {Object.values(routes).map(({ schema: path, element, label }) => (
          <Route path={path} element={element} key={label} />
        ))}
      </Route>
    )
  );

  return <ReactRouterProvider router={router} />;
};

export function WithLoadingState<T extends StudioPageProps>(
  WrappedComponent: React.ComponentType<T>
) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithLoadingState = (props: T) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { label: pageLabel } = useCurrentRoute();
    const currentPageDataLoading = useLoadingState() || isLoading;

    return (
      <>
        <div
          style={{
            ...centeredContainerStyles,
            display: currentPageDataLoading ? 'flex' : 'none',
          }}
        >
          <Loader relative />
        </div>
        <PageBody
          {...props.pageBodyProps}
          style={{
            display: currentPageDataLoading ? 'none' : 'block',
          }}
        >
          <WrappedComponent
            {...props}
            label={pageLabel}
            loading={currentPageDataLoading}
            setLoading={setIsLoading}
          />
        </PageBody>
      </>
    );
  };

  ComponentWithLoadingState.displayName = `WithLoadingState(${displayName})`;

  return ComponentWithLoadingState;
}
